import { Toast } from '@getvim/atomic-ui';
import { WriteBacks } from '@getvim/vim-connect';
import { useCallback } from 'react';
import { trackSelectWithVim } from '../../../../../analytics';
import { orderAssistLogger } from '../../../logger';
import {
  AppState,
  OrderAssistFormData,
  OrderAssistOption,
  OrderAssistFacilityOption,
  SelectActionMethod,
  SelectProviderAction,
  SelectProviderActionParams,
  SendOutcomeFunction,
  SourceConfigurations,
  WriteBackProvider,
} from '../../../types';
import addressFormat from '../../../utils/addressFormatter';
import formatForClipboard from '../../../utils/clipboardFormatter';
import { isProviderResult, isFacilityResult } from '../../../utils/general';
import { parseOptionToWritebackProvider } from '../../../utils/providerWritebackFormatter';
import { parseFacilityToWritebackProvider } from '../../../utils/facilityWritebackFormatter';
import { getSelectedItemAddress } from '../../../utils/search/getSelectedAddress';
import { useClipboard } from './useClipboard';
import { useWriteBack } from './use-referral-writeback';
import { getEhrSpecialtyByGarnerSpecialtyId } from '../../../consts/teladoc-garner/athenaGarnerSpecialtiesMapping';

type UseProviderSelectionProps = {
  onSelectionFinally: (selectedProvider: WriteBackProvider, referralUpdated: boolean) => void;
  sendOutcomeProps: {
    sendOutcomeFn: SendOutcomeFunction;
    extraInfo: { distanceInfo: string | undefined };
  };
  searchFormData: OrderAssistFormData;
  referralViewedPayload: AppState['referral'];
  isReferralRequestRequired: AppState['isReferralRequestRequired'];
  onWriteBackStart: () => void;
  onWriteBackFailed: () => void;
  updateReferralAllowed: boolean;
  updateOrderAllowed: boolean;
  sourceConfig: SourceConfigurations | undefined;
  adapterName: string | undefined;
};

export type GetSelectProviderActionType = (provider: OrderAssistOption) => WriteBacks.SelectAction;

export interface UseProviderSelectionReturn {
  select: SelectProviderAction;
  getSelectionType: GetSelectProviderActionType;
}

const successMessages: Record<WriteBacks.SelectAction, string> = {
  [WriteBacks.SelectAction.COPY_TO_CLIPBOARD]:
    'The details were copied to the clipboard, you can now paste them to any text field',
  [WriteBacks.SelectAction.WRITEBACK]: 'Your EHR was updated based on your selection',
};

interface OnSelectionFinallyDefaultOptions {
  useToast?: boolean;
  useAnalytics?: boolean;
}
interface OnSelectionFinallyDefaultParams {
  success: boolean;
  error: string;
  selectionType: WriteBacks.SelectAction;
  provider: OrderAssistOption;
  actionMethod: SelectActionMethod;
  isAlternative?: boolean;
  isPreferred?: boolean;
}

interface OnFinishSelectionAnalyticsParams {
  provider: OrderAssistOption;
  selectionType: WriteBacks.SelectAction;
  selectedAddress: string;
  acceptNewPatients?: boolean;
  isAlternative: boolean;
  isPreferred: boolean;
  actionMethod: SelectActionMethod;
}

const onFinishSelectionToast = async ({
  success,
  selectionType,
  error,
}: {
  success: boolean;
  selectionType: WriteBacks.SelectAction;
  error?: string;
}): Promise<void> => {
  const { createToast, ToastTypes } = Toast;

  const doneTitle = 'All done!';
  if (success) {
    createToast({
      title: doneTitle,
      message: successMessages[selectionType],
      type: ToastTypes.SUCCESS,
      html: true,
    });
  } else {
    const isProviderNotFoundWarn = error === 'ProviderNotFoundError';

    orderAssistLogger.info('Calculated isProviderNotFoundWarn', {
      noPHI: true,
      isProviderNotFoundWarn,
      error,
    });

    const title = isProviderNotFoundWarn ? doneTitle : 'Oops';
    const message = isProviderNotFoundWarn
      ? `Selected provider doesn't exist in your EHR \n` +
        `The details will appear in the notes section`
      : 'Something went wrong';
    const type = isProviderNotFoundWarn ? ToastTypes.WARNING : ToastTypes.ERROR;

    createToast({
      title,
      message,
      type,
      html: true,
    });
  }
};

export const useProviderSelection = ({
  onSelectionFinally,
  referralViewedPayload,
  isReferralRequestRequired,
  sendOutcomeProps,
  searchFormData,
  onWriteBackStart,
  onWriteBackFailed,
  updateReferralAllowed,
  updateOrderAllowed,
  sourceConfig,
  adapterName,
}: UseProviderSelectionProps): UseProviderSelectionReturn => {
  const { writeProvider, writeFacility } = useWriteBack();
  const copy = useClipboard();
  const { extraInfo, sendOutcomeFn } = sendOutcomeProps;

  const getSelectionType: GetSelectProviderActionType = useCallback(
    (provider) => {
      // Enable writeback for facility in order context
      if (updateOrderAllowed && isFacilityResult(provider)) {
        return WriteBacks.SelectAction.WRITEBACK;
      }
      // Enable writeback for provider in referral context
      if (updateReferralAllowed && isProviderResult(provider)) {
        return WriteBacks.SelectAction.WRITEBACK;
      }
      // Default to clipboard
      return WriteBacks.SelectAction.COPY_TO_CLIPBOARD;
    },
    [updateReferralAllowed, updateOrderAllowed],
  );

  const onFinishSelectionAnalytics = useCallback(
    (success: boolean, params: OnFinishSelectionAnalyticsParams) => {
      const {
        isAlternative,
        isPreferred,
        provider,
        selectedAddress,
        acceptNewPatients,
        selectionType,
        actionMethod,
      } = params;
      trackSelectWithVim({
        selectAction: selectionType,
        status: success ? WriteBacks.ActionStatus.SUCCESS : WriteBacks.ActionStatus.FAILED,
        isAlternativeResult: isAlternative,
        selectedOption: provider,
        selectedAddress,
        acceptNewPatients,
        previousProvider: referralViewedPayload?.targetProvider,
        isPreferredResult: isPreferred,
        isReferralRequestRequired,
        method: actionMethod,
      });
    },
    [isReferralRequestRequired, referralViewedPayload?.targetProvider],
  );

  const onSelectionFinallyDefault = useCallback(
    async (
      params: OnSelectionFinallyDefaultParams,
      options?: OnSelectionFinallyDefaultOptions | undefined,
    ) => {
      const {
        success,
        provider,
        selectionType,
        isAlternative = false,
        isPreferred = false,
        actionMethod,
        error,
      } = params;

      const selectedItemAddress = getSelectedItemAddress(provider);
      const selectedAddress = addressFormat(selectedItemAddress);

      const { acceptNewPatients } = selectedItemAddress;

      if (options?.useToast) await onFinishSelectionToast({ success, error, selectionType });
      if (options?.useAnalytics)
        onFinishSelectionAnalytics(success, {
          isAlternative,
          isPreferred,
          provider,
          selectedAddress,
          acceptNewPatients,
          selectionType,
          actionMethod,
        });
      sendOutcomeFn({
        item: provider,
        isAlternative,
        selectedLocation: getSelectedItemAddress(provider),
        currentSearchState: searchFormData,
        extraInfo,
      });
    },
    [extraInfo, onFinishSelectionAnalytics, searchFormData, sendOutcomeFn],
  );

  const select: SelectProviderAction = useCallback(
    async ({
      provider,
      isAlternative,
      isPreferred,
      actionMethod,
      lastEhrSpecialtySearch,
    }: SelectProviderActionParams) => {
      let success = false;
      let error;
      let hasWrittenBack = false;
      const selectionType = getSelectionType(provider);
      if (!selectionType) return { success: false, selectionType };

      const ehrSpecialty = getEhrSpecialtyByGarnerSpecialtyId({
        adapterName,
        garnerSpecialtyId: provider.details.specialties?.[0]?.id,
        lastEhrSpecialtySearch,
      });

      // Parse to appropriate writeback format based on type
      const writeBackData: WriteBackProvider = isProviderResult(provider)
        ? parseOptionToWritebackProvider(provider, ehrSpecialty)
        : parseFacilityToWritebackProvider(provider as OrderAssistFacilityOption, ehrSpecialty);

      try {
        switch (selectionType) {
          case WriteBacks.SelectAction.COPY_TO_CLIPBOARD: {
            const text = formatForClipboard({
              option: provider,
              supportScoreRatingBadges: sourceConfig?.supportScoreRatingBadges,
              supportNetworkStatusBadge: sourceConfig?.supportNetworkStatusBadge,
              supportOpeningHours: sourceConfig?.supportOpeningHours,
              displayDistanceInExportDetails: sourceConfig?.displayDistanceInExportDetails,
              supportAcceptingNewPatientsInCard: sourceConfig?.supportAcceptingNewPatientsInCard,
            });

            success = await copy(text);
            break;
          }
          case WriteBacks.SelectAction.WRITEBACK: {
            onWriteBackStart();

            // Call appropriate writeback function based on type
            const result = isFacilityResult(provider)
              ? await writeFacility(writeBackData)
              : await writeProvider(writeBackData);

            success = result.success;
            error = result?.error;

            if (!success) {
              onWriteBackFailed();
            }

            // Track that writeback was attempted (for referrals or orders)
            hasWrittenBack = true;

            break;
          }
          default: {
            orderAssistLogger.warning('Got an unexpected selection type!', { selectionType });
            break;
          }
        }
      } catch (error) {
        orderAssistLogger.warning('Error occurred when selecting provider', {
          selectionType,
          provider,
          error,
        });
      } finally {
        await onSelectionFinallyDefault(
          { success, provider, selectionType, isAlternative, isPreferred, actionMethod, error },
          { useAnalytics: true, useToast: true },
        );
        onSelectionFinally(writeBackData, hasWrittenBack);
      }
      return { selectionType, success };
    },
    [
      getSelectionType,
      copy,
      onWriteBackStart,
      writeProvider,
      writeFacility,
      onWriteBackFailed,
      onSelectionFinallyDefault,
      onSelectionFinally,
      adapterName,
    ],
  );

  return { select, getSelectionType };
};
